import { useState } from 'react';

import { Flag02Icon } from '@hugeicons-pro/core-solid-rounded';
import { BubbleChatQuestionIcon, User02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { LANGUAGE_MAP } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { FieldTypesOptions } from 'constants/field-types-enum';
import { FLAG_SEVERITY_CARD_STYLES, FLAG_SEVERITY_MAP } from 'constants/forms';
import { saveAs } from 'file-saver';
import {
  downloadAllAsZip as downloadAllAsZipUtil,
  getFilenameFromUrl,
} from 'helpers/document-download-helpers';
import { escapeHtml, sanitizeUrl } from 'helpers/form';

import StatusPill from 'components/Pills/StatusPills';

import { FormattedAnswer } from '../../hooks/useFormattedAnswer';
import styles from './QuestionResponses.module.scss';

const AnswerWithTranslation = ({
  field,
  answer,
  translatedText,
  languageCode,
  translationScore,
  onDownload,
  onDownloadAllAsZip,
  zipLoading,
}) => {
  const formattedAnswer = (
    <FormattedAnswer
      field={field}
      answer={answer}
      onDownload={onDownload}
      onDownloadAllAsZip={onDownloadAllAsZip}
      zipLoading={zipLoading}
    />
  );

  if (!translatedText) {
    return <div className={styles.answer}>{formattedAnswer}</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xl font-medium text-neutral-7">
            English translation (auto-generated)
          </span>
          {translationScore != null && (
            <StatusPill
              text={'Confidence: ' + Math.round(translationScore * 100) + '%'}
              status={translationScore >= 0.7 ? 'success' : 'warning'}
              small
            />
          )}
        </div>
        <div className={styles.answer}>{translatedText}</div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xl font-medium text-neutral-7">Original response</span>
          <StatusPill
            text={LANGUAGE_MAP[languageCode] || languageCode || 'Translated'}
            status="info"
            small
          />
        </div>
        <div className={cn('rounded-3xl border p-4 bg-neutral-2', styles.answer)}>
          {formattedAnswer}
        </div>
      </div>
    </div>
  );
};

export const QuestionResponses = ({ order, field, params = {} }) => {
  const [zipLoading, setZipLoading] = useState(false);

  const downloadSingle = url => {
    if (!url) return;
    saveAs(url, getFilenameFromUrl(url, 0));
  };

  const downloadAllAsZip = (urls, zipName = 'documents.zip') =>
    downloadAllAsZipUtil(urls, zipName, {
      onStart: () => setZipLoading(true),
      onEnd: () => setZipLoading(false),
    });

  const formatQuestion = text => {
    if (!text) return;

    const textWithLinks = text.replace(/\[((?!\]).)*\]\(((?!\)).)*\)/g, match => {
      const [, linkText] = /\[([^)]+)\]/.exec(match);
      const [, link] = /\(([^)]+)\)/.exec(match);

      return `<a href="${escapeHtml(sanitizeUrl(link))}" class='u-link dark' target="_blank">${escapeHtml(linkText)}</a>`;
    });

    const textWithVars = textWithLinks.replace(/{{((?!}).)*}}/g, match => {
      const m = match.replace(/[{}]/g, '');
      const value = params[m];
      const text = value ? value : isNaN(m) ? m : `Q${m} answer`;

      return `<b>${escapeHtml(text)}</b>`;
    });

    return textWithVars;
  };

  return (
    <div className="border border-neutral-4 rounded-3xl shadow-sm bg-neutral-2 p-8">
      <div className="flex items-start gap-3 mb-4">
        <span className="text-neutral-1 text-lg font-medium bg-neutral-6 rounded-full w-10 h-10 flex items-center justify-center shrink-0">
          {order || field.order}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <HugeiconsIcon
              icon={
                FieldTypesOptions.find(option => option.key === field.type)?.icon ??
                BubbleChatQuestionIcon
              }
              size={20}
              className="text-text-tertiary"
            />
            <span className="text-lg font-regular text-neutral-6 uppercase tracking-wide">
              {FieldTypesOptions.find(option => option.key === field.type)?.title}
            </span>
          </div>
          <div
            className="text-2xl font-medium text-neutral-7 leading-snug"
            dangerouslySetInnerHTML={{ __html: formatQuestion(field.text) }}
          />
        </div>
      </div>
      {field.type !== FieldTypes.Statement && (
        <div className="flex flex-col gap-4">
          {field.answers.map((answer, i) => {
            const flagStyles =
              answer.flagSeverity && FLAG_SEVERITY_MAP[answer.flagSeverity]
                ? FLAG_SEVERITY_CARD_STYLES[answer.flagSeverity]
                : null;
            return (
              <div
                key={i}
                className={cn(
                  'transition-all duration-300 rounded-2xl border p-6',
                  flagStyles
                    ? `${flagStyles.bg} ${flagStyles.border} hover:opacity-95`
                    : 'bg-neutral-1 border-neutral-4 hover:border-neutral-5',
                )}>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <StatusPill
                      text={answer.name}
                      status="light"
                      icon={User02Icon}
                      iconColor="text-neutral-6"
                      small
                      className="px-4 py-2"
                    />
                    {answer.flagSeverity && FLAG_SEVERITY_MAP[answer.flagSeverity] && (
                      <StatusPill
                        text={FLAG_SEVERITY_MAP[answer.flagSeverity].label}
                        status={FLAG_SEVERITY_MAP[answer.flagSeverity].status}
                        icon={Flag02Icon}
                        iconColor={flagStyles?.color}
                        small
                        className="px-4 py-2 border-none"
                      />
                    )}
                  </div>
                  <div className="text-xl">
                    <AnswerWithTranslation
                      field={field}
                      answer={answer.answer}
                      translatedText={answer.translated_text}
                      languageCode={answer.language_code}
                      translationScore={answer.translation_score}
                      onDownload={downloadSingle}
                      onDownloadAllAsZip={downloadAllAsZip}
                      zipLoading={zipLoading}
                    />
                  </div>
                </div>
                {answer.subFields?.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-neutral-4 flex flex-col gap-4">
                    {answer.subFields?.map((subfield, subIdx) => (
                      <div key={subIdx} className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-6 text-sm font-medium">
                            {order || field.order}.{String.fromCharCode(65 + subIdx)}.
                          </span>
                          <div
                            className="text-lg font-regular text-neutral-7"
                            dangerouslySetInnerHTML={{
                              __html: formatQuestion(subfield.text),
                            }}
                          />
                        </div>
                        <div className="rounded-2xl border border-neutral-4 bg-neutral-1 p-4">
                          <div className="text-xl">
                            <AnswerWithTranslation
                              field={subfield}
                              answer={subfield.answer}
                              translatedText={subfield.translated_text}
                              languageCode={subfield.language_code}
                              translationScore={subfield.translation_score}
                              onDownload={downloadSingle}
                              onDownloadAllAsZip={downloadAllAsZip}
                              zipLoading={zipLoading}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
